import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';

export const availableLanguages = [
  { code: 'en', name: 'English' },
  { code: 'el', name: 'Ελληνικά' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'pt', name: 'Português' },
] as const;
export type Language = typeof availableLanguages[number]['code'];
const supported = (value?: string | null): Language | undefined => {
  const code = value?.toLowerCase().split('-')[0];
  return availableLanguages.some(item => item.code === code) ? code as Language : undefined;
};
interface LanguageContextType {
  currentLanguage: Language;
  isChanging: boolean;
  changeLanguage: (language: string) => Promise<void>;
  availableLanguages: typeof availableLanguages;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const { profile } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => supported(i18n.resolvedLanguage || i18n.language) || 'en');
  const [isChanging, setIsChanging] = useState(false);
  const changing = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let stored: string | null = null;
    try { stored = localStorage.getItem('warriorhub-language'); } catch { /* Storage may be disabled. */ }
    const language = supported(profile?.preferred_language) || supported(stored) || supported(typeof navigator === 'undefined' ? '' : navigator.language) || 'en';
    i18n.changeLanguage(language).then(() => { if (!cancelled) setCurrentLanguage(language); }).catch(console.error);
    return () => { cancelled = true; };
    // Initialize on account changes, not unrelated profile refreshes which could undo a selection.
  }, [profile?.id, i18n]);

  const changeLanguage = async (value: string) => {
    const language = supported(value);
    if (!language || changing.current || language === currentLanguage) return;
    changing.current = true;
    setIsChanging(true);
    try {
      await i18n.changeLanguage(language);
      setCurrentLanguage(language);
      try { localStorage.setItem('warriorhub-language', language); } catch { /* Keep the active selection. */ }
      if (profile?.id) {
        const { error } = await supabase.from('profiles').update({ preferred_language: language }).eq('id', profile.id);
        if (error) console.error('Could not save language preference:', error);
      }
    } finally {
      changing.current = false;
      setIsChanging(false);
    }
  };

  return <LanguageContext.Provider value={{ currentLanguage, changeLanguage, availableLanguages, isChanging }}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
}
